import React, { useRef, useState } from "react";
import styled from "@emotion/styled";
import Cropper from "react-avatar-editor";
import { useRecoilState } from "recoil";
import StyledButton from "../../StyledButton";
import { notSelect } from "../../../styles/general";
import { UserAtom } from "../../../atoms/UserAtom";
import { updateUser } from "../../../api/User";
import { useModal } from "../Modal";
import { uploadImage } from "../../../api/Images";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  border-top: 1px solid #eceeee;
  box-sizing: inherit;
  margin: 20px -22px 0px;
`;

const StyledCropper = styled(Cropper)`
  margin: 20px;
  border-radius: 8px;
`;

const RangeDiv = styled.div`
  height: 40px;
  background-color: #eaeaea;
  border-radius: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
`;

const Range = styled.input`
  width: 250px;
  height: 4px;
  border-radius: 1px;

  & {
    margin-top: 3px;
    height: 4px;
    -webkit-appearance: none;
    background-color: #6a6f74;
  }
  &::-webkit-slider-runnable-track {
    height: 40px;
    -webkit-appearance: none;
    color: blue;
    transition: box-shadow 0.2s ease-in-out;
    margin-top: 20px;
  }
  &::-webkit-slider-thumb {
    width: 20px;
    -webkit-appearance: none;
    height: 20px;
    cursor: ew-resize;
    background: #fb972e;
    box-shadow: inset 0 0 0 40px #fff;
    border-radius: 50%;
    transition: box-shadow 0.2s ease-in-out;
    position: relative;
  }
  &:active::-webkit-slider-thumb {
    background: #fb972e;
    box-shadow: inset 0 0 0 3px #fff;
  }
`;

const Buttons = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: flex-end;
  border-top: 1px solid #eceeee;
  padding: 20px 20px 0px;
`;

const IconContainer = styled.div`
  margin: 0px 10px;
  cursor: pointer;
  width: 20px;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  ${notSelect}
`;

const Icon = styled.img`
  width: 15px;
  height: 15px;
`;

interface Props {
  imageSrc: string;
  closeModal: () => void;
  saveImage: (croppedImage: Blob) => void;
}

export default function UpdateProfilePhoto(props: Props): JSX.Element {
  const { imageSrc, saveImage, closeModal } = props;
  const [zoom, setZoom] = useState(1);
  const editorRef = useRef<any>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [user, setUser] = useRecoilState(UserAtom);
  const modal = useModal();

  async function sendUpdateUser(imageId: string | null) {
    if (!user) return false;
    const userObject = {
      ...user,
      avatar: imageId as string,
    };

    delete (userObject as any)._id;
    const success = await updateUser(userObject);

    if (success) {
      setUser({
        ...user,
        avatar: imageId as string,
      });
      modal.popModal();
    }
    return false;
  }

  async function updateProfileImage(croppedImage: Blob) {
    setImageLoading(true);
    const response = await uploadImage(croppedImage);
    if (response.status === 200) {
      await sendUpdateUser(response.id);
      setImageLoading(false);
    }
  }

  function getCroppedImage() {
    const croppedImage = editorRef.current.getImage().toDataURL();

    fetch(croppedImage)
      .then((res) => res.blob())
      .then((blob) => {
        saveImage(blob);
        updateProfileImage(blob);
      });
  }

  return (
    <Container>
      <StyledCropper
        ref={editorRef}
        image={imageSrc}
        scale={zoom}
        width={320}
        height={320}
        borderRadius={300}
        border={0}
      />
      <RangeDiv>
        <IconContainer
          onClick={() => {
            if (zoom > 1) setZoom(zoom - 0.1);
          }}
        >
          <Icon src={"./icons/minus.svg"} />
        </IconContainer>

        <Range
          type="range"
          min="1"
          max="3"
          step="0.1"
          value={zoom}
          onChange={(event: any) => {
            setZoom(parseFloat(event.target.value));
          }}
        />
        <IconContainer
          onClick={() => {
            if (zoom < 3) setZoom(zoom + 0.1);
          }}
        >
          <Icon src={"./icons/plus-grey.svg"} />
        </IconContainer>
      </RangeDiv>
      <Buttons>
        <StyledButton
          style={{ marginRight: "12px", width: "100px", height: "35px" }}
          onClick={closeModal}
        >
          Cancel
        </StyledButton>
        <StyledButton
          style={{ width: "100px", height: "35px" }}
          inverted
          onClick={getCroppedImage}
          isLoading={imageLoading}
        >
          Save
        </StyledButton>
      </Buttons>
    </Container>
  );
}
